import { useState, useEffect } from "react";
import { getReferralStats, getReferralCoupons, getReferralSettings } from "../../services/master.service";
import { 
  Box, 
  Container, 
  Text, 
  Paper, 
  RingProgress, 
  Group, 
  Stack, 
  Button, 
  ThemeIcon, 
  Divider,
  SimpleGrid,
  CopyButton,
  ActionIcon,
  Tooltip,
  Flex,
  Badge
} from "@mantine/core";
import { 
  IconGift, 
  IconShare, 
  IconUsers, 
  IconCopy, 
  IconCheck, 
  IconTrophy,
  IconTicket
} from "@tabler/icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "@mantine/hooks";
import { showSuccessToast } from "../../utilities/Toast";
import { is_mobile_view } from "../../config";

export default function ReferralCoupon() {
  const [targetInvites, setTargetInvites] = useState(4);
  const isReferralMobile = useMediaQuery("(max-width: 480px)") || is_mobile_view;
  const [referralCount, setReferralCount] = useState(0);
  const [rewardsEarned, setRewardsEarned] = useState(0);
  const [coupons, setCoupons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const referralCode = user?.referral_code || "QUZONE2024";

  const percentage = targetInvites > 0 ? Math.min(Math.round((referralCount / targetInvites) * 100), 100) : 0;

  useEffect(() => {
    fetchStats();
    fetchCoupons();
  }, []);

  const fetchStats = async () => {
    try {
      let currentTarget = 4;
      try {
        const settingsRes = await getReferralSettings();
        if (settingsRes?.data?.success && settingsRes?.data?.data) {
          currentTarget = settingsRes.data.data.referral_invite_count ?? 4;
          setTargetInvites(currentTarget);
        }
      } catch (err) {
        console.error("Failed to fetch referral settings", err);
      }

      const res = await getReferralStats();
      const data = res?.data;
      const total = data?.total_referrals ?? 0;
      const rewarded = data?.rewarded_referrals ?? 0;
      const earned = Math.floor(rewarded / currentTarget);
      let progress = total % currentTarget;
      if (progress === 0 && total > 0) progress = currentTarget;
      setReferralCount(progress);
      setRewardsEarned(earned);
    } catch (error) {
      console.error("Referral stats fetch failed", error);
    }
  };

  const fetchCoupons = async () => {
    try {
      const res = await getReferralCoupons();
      setCoupons(res?.data?.data ?? []);
      setIsLoading(false);
    } catch (error) {
      console.error("Referral coupon fetch failed", error);
      setIsLoading(false);
    }
  };

  return (
    <Container size="sm" py={20}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <Stack spacing="xl">
          {/* HEADER STATS */}
          <Paper radius="xl" p={isReferralMobile ? "lg" : "xl"} sx={(theme) => ({
            background: 'linear-gradient(135deg, #22c55e 0%, #15803d 100%)',
            color: theme.white,
            position: 'relative',
            overflow: 'hidden'
          })}>
            <Box sx={{ position: 'absolute', top: -20, right: -20, opacity: 0.1 }}>
              <IconGift size={isReferralMobile ? 120 : 150} />
            </Box>
            
            <Flex direction="row" align="center" gap={isReferralMobile ? "md" : "xl"}>
              <RingProgress
                size={isReferralMobile ? 96 : 120}
                thickness={isReferralMobile ? 8 : 10}
                roundCaps
                label={
                  <Text size={isReferralMobile ? "sm" : "lg"} align="center" fw={800} color="white">
                    {percentage}%
                  </Text>
                }
                sections={[{ value: percentage, color: 'rgba(255,255,255,0.9)' }]}
              />
              <Box sx={{ textAlign: 'left', minWidth: 0, flex: 1 }}>
                <Text fw={800} size={isReferralMobile ? 21 : 24} lh={1.18}>Refer & Earn Rewards</Text>
                <Text size={isReferralMobile ? "xs" : "sm"} opacity={0.8} mb={isReferralMobile ? 10 : "md"} lh={1.35}>
                  Invite your friends and unlock special coupons!
                </Text>
                <Group position="left" spacing={0}>
                  <Badge
                    size={isReferralMobile ? "md" : "lg"}
                    radius="md"
                    color="yellow"
                    variant="filled"
                    leftSection={<IconTrophy size={isReferralMobile ? 12 : 14} />}
                    sx={{
                      maxWidth: '100%',
                      '& .mantine-Badge-inner': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  >
                    {rewardsEarned} Rewards Won
                  </Badge>
                </Group>
              </Box>
            </Flex>
          </Paper>

          {/* REFERRAL CODE BOX */}
          <Paper p="xl" radius="lg" withBorder shadow="sm">
            <Text fw={700} mb="md" size="sm" color="dimmed" tt="uppercase">Your Referral Code</Text>
            <Group position="apart">
              <Box p="md" sx={(theme) => ({ 
                backgroundColor: theme.colors.gray[0], 
                borderRadius: theme.radius.md,
                border: `2px dashed ${theme.colors.gray[3]}`,
                flex: 1
              })}>
                <Text fw={900} size="xl" ta="center" sx={{ letterSpacing: 2 }}>{referralCode}</Text>
              </Box>
              <CopyButton value={referralCode} timeout={2000}>
                {({ copied, copy }) => (
                  <Button 
                    color={copied ? 'teal' : 'blue'} 
                    onClick={copy}
                    size="lg"
                    radius="md"
                    leftIcon={copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                  >
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                )}
              </CopyButton>
            </Group>
          </Paper>

          {/* COUPONS SECTION */}
          <Box mt="lg">
            <Group position="apart" mb="md">
              <Group>
                <ThemeIcon radius="md" size={32} color="pink" variant="light">
                  <IconTicket size={20} />
                </ThemeIcon>
                <Text fw={800} size="lg">Available Coupons</Text>
              </Group>
            </Group>

            <AnimatePresence>
              {coupons.length > 0 ? (
                <SimpleGrid cols={isReferralMobile ? 1 : 2} spacing="md">
                  {coupons.map((coupon, idx) => (
                    <motion.div 
                      key={idx}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: idx * 0.1 }}
                    >
                      <Paper p="md" radius="md" withBorder sx={(theme) => ({
                        position: 'relative',
                        backgroundColor: '#fff',
                        borderLeft: `6px solid ${coupon.source === 'welcome' ? theme.colors.blue[5] : theme.colors.green[5]}`,
                        '&::before, &::after': {
                          content: '""',
                          position: 'absolute',
                          width: 20,
                          height: 20,
                          backgroundColor: theme.colors.gray[0],
                          borderRadius: '50%',
                          left: -13,
                        },
                        '&::before': { top: 'calc(50% - 10px)' },
                      })}>
                        <Group position="apart">
                          <Box>
                            <Text fw={800} color={coupon.source === 'welcome' ? 'blue' : 'green'}>{coupon.code}</Text>
                            <Text size={10} color="dimmed" tt="uppercase" mt={2}>
                              {coupon.source === 'welcome' ? 'Welcome Gift' : 'Referral Reward'}
                            </Text>
                          </Box>
                          <CopyButton value={coupon.code} timeout={2000}>
                            {({ copied, copy }) => (
                              <Tooltip label={copied ? 'Copied!' : 'Copy Code'}>
                                <ActionIcon color={copied ? 'teal' : 'gray'} onClick={copy} variant="light" size="lg">
                                  {copied ? <IconCheck size={18} /> : <IconCopy size={18} />}
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </CopyButton>
                        </Group>
                      </Paper>
                    </motion.div>
                  ))}
                </SimpleGrid>
              ) : (
                <Paper p="xl" radius="lg" withBorder sx={{ textAlign: 'center', borderStyle: 'dashed' }}>
                  <Text color="dimmed">No coupons available. Start referring to unlock rewards!</Text>
                </Paper>
              )}
            </AnimatePresence>
          </Box>
        </Stack>
      </motion.div>
    </Container>
  );
}
